import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import {ContractRequest, ContractResponse} from '@models/contract.model';
import {ContractService} from '@services/contract.service';
import {saveAs} from 'file-saver';
import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import {FuseAlertType} from '@fuse/components/alert';
import {finalize, Subject, takeUntil} from 'rxjs';
import {RP_STATUS, RPStatus} from '@enums/RPStatus';
import {CONTRACT_STATUS} from '@enums/ContractStatusEnum';
import {fuseAnimations} from '@fuse/animations';
import {Provider} from '@hotels/providers/contacts.types';
import {AccountRole} from '@enums/AccountRole';
import {User} from '@models/user.interface';
import {UserService} from '@core/user/user.service';
import {UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {FuseConfirmationService} from '@fuse/services/confirmation';
import {MatPaginator} from '@angular/material/paginator';
import {MatTableDataSource} from '@angular/material/table';
import {MatDialog, MatDialogRef} from '@angular/material/dialog';
import {Page} from '@utils/paginator-util';
import {MatSort} from '@angular/material/sort';
import {FuseValidators} from '@fuse/validators/validators';
import {ImssEmployerRegistryResponse} from '@models/imss-employer-registry.interface';
import {ContractStatus} from '@models/contract-status.interface';
import {ImssEmployerRegistryService} from '@services/imss-employer-registry.service';
import {FuseConfirmationDialogComponent} from '@fuse/services/confirmation/dialog/dialog.component';
import moment from 'moment';
import {ProviderStatus} from '@enums/ProviderStatus';

@Component({
    selector: 'app-contracts',
    templateUrl: './contracts.component.html',
    styleUrls: ['./contracts.component.scss'],
    animations: fuseAnimations,
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ContractsComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {

    @Input() contracteeId: number;
    @Input() contractor: Provider;
    @Input() contractorStatus: ProviderStatus;
    @Input() isFianzasEnabled: boolean = false;
    @Input() contracteeName?: string;   // nombre de la compañía/contratante (para notificación Discord de fianzas)
    @ViewChild('toggleContractFormDialogTemplate') toggleContractFormDialogTemplate: TemplateRef<any>;
    @ViewChild('toggleAddRpToContractDialogTemplate') toggleAddRpToContractDialogTemplate: TemplateRef<any>;
    @ViewChild('fianzasDialogTemplate') fianzasDialogTemplate: TemplateRef<any>;
    @ViewChild(MatPaginator) paginator: MatPaginator;
    @ViewChild(MatSort) sort: MatSort;

    contracts: ContractResponse[];
    selectedContractUuid: string | null = null;
    selectedContractTag: string | null = null;
    totalContracts: number;
    imssEmployerRegistries: ImssEmployerRegistryResponse[];
    displayedColumns: string[] = ['actions', 'status', 'tag', 'startDate', 'endDate', 'rp', 'region', 'uuid'];
    dataSource: MatTableDataSource<ContractResponse> = new MatTableDataSource<ContractResponse>([]);
    alertDownloadContract: { type: FuseAlertType; message: string } = {type: 'info', message: ''};
    alertRpStatus: { type: FuseAlertType; message: string } = {type: 'info', message: ''};
    alertCreateOrUpdateContract: { type: FuseAlertType; message: string } = {type: 'info', message: ''};
    alertDeleteContract: { type: FuseAlertType; message: string } = {type: 'info', message: ''};
    minDate: Date = new Date(2021, 0, 1);
    maxDate: Date = new Date(2099, 0, 1);
    readonly accountRole: typeof AccountRole = AccountRole;
    contractStatusList: ContractStatus[] = [];
    user: User;
    contractForm: UntypedFormGroup;
    addRpForm: UntypedFormGroup;
    isUpdate: boolean = false;
    isSubmitting: boolean = false;
    protected readonly rpStatus: typeof RP_STATUS = RP_STATUS;
    private readonly alertTime: number = 5000;
    private readonly maxSizeFile: number = 10485760; // 10 MB
    private _showAlertDownloadContract: boolean = false;
    private _showAlertSetRpStatus: boolean = false;
    private _showAlertCreateOrUpdateContract: boolean = false;
    private _showAlertDeleteContract: boolean = false;
    private readonly _unsubscribeAll$: Subject<any> = new Subject<any>();

    constructor(private readonly _cdr: ChangeDetectorRef,
                private readonly _fb: UntypedFormBuilder,
                private readonly _fuseConfirmationService: FuseConfirmationService,
                public dialog: MatDialog,
                private readonly _userService: UserService,
                private readonly _imssEmployerRegistryService: ImssEmployerRegistryService,
                private readonly _service: ContractService) {
    }

    get showAlertDownloadContract(): boolean {
        return this._showAlertDownloadContract;
    }

    set showAlertDownloadContract(val: boolean) {
        this._showAlertDownloadContract = val;
        this._cdr.markForCheck();
        if (val) {
            setTimeout(() => {
                this._showAlertDownloadContract = false;
                this._cdr.markForCheck();
            }, this.alertTime);
        }
    }

    get showAlertSetRpStatus(): boolean {
        return this._showAlertSetRpStatus;
    }

    set showAlertSetRpStatus(val: boolean) {
        this._showAlertSetRpStatus = val;
        this._cdr.markForCheck();
        if (val) {
            setTimeout(() => {
                this._showAlertSetRpStatus = false;
                this._cdr.markForCheck();
            }, this.alertTime);
        }
    }

    get showAlertCreateOrUpdateContract(): boolean {
        return this._showAlertCreateOrUpdateContract;
    }

    set showAlertCreateOrUpdateContract(val: boolean) {
        this._showAlertCreateOrUpdateContract = val;
        this._cdr.markForCheck();
        if (val) {
            setTimeout(() => {
                this._showAlertCreateOrUpdateContract = false;
                this._cdr.markForCheck();
            }, this.alertTime);
        }
    }

    get showAlertDeleteContract(): boolean {
        return this._showAlertDeleteContract;
    }

    set showAlertDeleteContract(val: boolean) {
        this._showAlertDeleteContract = val;
        this._cdr.markForCheck();
        if (val) {
            setTimeout(() => {
                this._showAlertDeleteContract = false;
                this._cdr.markForCheck();
            }, this.alertTime);
        }
    }

    get hasRegistriesWithAltaPatronal(): boolean {
        return this.imssEmployerRegistries?.some(r => !!r.altaPatronalUrl) ?? false;
    }

    get isDateRangeInvalid(): boolean {
        return !!this.contractForm &&
            this.contractForm.hasError('dateRange') &&
            (this.contractForm.get('startDate')?.touched || this.contractForm.get('endDate')?.touched);
    }

    ngOnInit(): void {
        this._service.getStatusList().pipe(takeUntil(this._unsubscribeAll$))
            .subscribe((statusList: ContractStatus[]): void => {
                this.contractStatusList = statusList;
            });
        this._userService.user$.pipe(takeUntil(this._unsubscribeAll$)).subscribe((user: User) => {
            this.user = user;
        });
        if (this.user.rol === AccountRole.provider) this.getRpList();

        this.contractForm = this._fb.group({
            id: [''],
            uuid: [''],
            rp: [''],
            isImssEmployerRegistryRequested: [RP_STATUS.withoutRp, Validators.required],
            tag: ['', [Validators.maxLength(30)]],
            region: [0, [Validators.min(0)]],
            status: [CONTRACT_STATUS.active, [Validators.required]],
            startDate: ['', [Validators.required]],
            endDate: ['', [Validators.required]],
            amount: ['', [Validators.min(0), Validators.max(9999999999.99)]],
            subject: ['', [Validators.minLength(3), Validators.maxLength(500)]],
            file: [undefined, [FuseValidators.maxContentSize(this.maxSizeFile)]],
            synchronizeEvidenceConfig: [false],
            recalculateEvidenceSummaries: [{value: false, disabled: true}]
        }, {
            validators: [this.dateRangeValidator]
        });
        this.contractForm.get('synchronizeEvidenceConfig')?.valueChanges
            .pipe(takeUntil(this._unsubscribeAll$))
            .subscribe((synchronizeEvidenceConfig: boolean): void => {
                this.toggleRecalculateEvidenceSummariesControl(synchronizeEvidenceConfig);
            });
        this.addRpForm = this._fb.group({
            rp: ['', [Validators.required]]
        });
    }

    ngAfterViewInit(): void {
        this.paginator.page.pipe(takeUntil(this._unsubscribeAll$)).subscribe((): void => {
            this.getContracts(this.paginator, this.sort);
        });

        this.sort.sortChange.pipe(takeUntil(this._unsubscribeAll$)).subscribe((): void => {
            if (this.paginator) this.paginator.pageIndex = 0;
            this.getContracts(this.paginator, this.sort);
        });

        this.getContracts(this.paginator, this.sort);
    }

    ngOnChanges(): void {
        this.resetSortAndPaginator();
        this.getContracts(this.paginator, this.sort);
    }

    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll$.next(null);
        this._unsubscribeAll$.complete();
    }

    canSeeContractEvidences(): boolean {
        // If user is a contractor (provider) his status must be known and cannot be inactive to see the contract evidences
        const ifUserIsContractorMustBeActive: boolean = this.user.rol === AccountRole.provider
            ? this.isValidProviderStatus(this.contractorStatus)
            : true;
        return !!this.contractor?.enabled && ifUserIsContractorMustBeActive;
    }

    createOrUpdateContract(): void {
        this.showAlertCreateOrUpdateContract = false;
        if (this.contractForm.invalid) return;
        this.isSubmitting = true;

        const formData: FormData = new FormData();
        const contractFormValue: any = this.contractForm.getRawValue();
        const file: File | null = contractFormValue.file;

        if (contractFormValue.file) {
            if (!file) {
                console.error('No file selected or invalid file.');
                this.isSubmitting = false;
                this.alertCreateOrUpdateContract = {
                    type: 'error',
                    message: 'Por favor, seleccione un archivo válido para el contrato.'
                };
                this.showAlertCreateOrUpdateContract = true;
                return;
            }

            formData.append('file', file, file.name);
        }

        const contractData: ContractRequest = {
            id: contractFormValue.id,
            uuid: contractFormValue.uuid,
            contractorId: Number(this.contractor.id),
            contracteeId: this.contracteeId,
            hotelCompanyId: null,
            rpId: contractFormValue.rp,
            isImssEmployerRegistryRequested: contractFormValue.isImssEmployerRegistryRequested,
            tag: contractFormValue.tag,
            amount: contractFormValue.amount,
            subject: contractFormValue.subject,
            regionId: contractFormValue.region === 0 ? null : contractFormValue.region,
            statusId: contractFormValue.status,
            startDate: contractFormValue.startDate,
            endDate: contractFormValue.endDate
        };

        if (this.isUpdate) {
            contractData.synchronizeEvidenceConfig = !!contractFormValue.synchronizeEvidenceConfig;
            contractData.recalculateEvidenceSummaries = !!contractFormValue.synchronizeEvidenceConfig
                && !!contractFormValue.recalculateEvidenceSummaries;
        }

        formData.append('contractData', new Blob([JSON.stringify(contractData)], {type: 'application/json'}));

        if (this.isUpdate) {
            this._service.updateContract(formData, contractData.id).pipe(finalize((): void => {
                this.contractForm.reset();
                this.showAlertCreateOrUpdateContract = true;
                this.isSubmitting = false;
                this.dialog.closeAll();
            })).subscribe({
                next: (updatedContract: ContractResponse): void => {
                    this.alertCreateOrUpdateContract = {
                        type: 'success',
                        message: `El contrato con folio ${updatedContract.tag} se ha actualizado correctamente.`
                    };
                    this.getContracts(this.paginator, this.sort);
                },
                error: (err: HttpErrorResponse): void => {
                    console.error('Error updating contract:', err.message);
                    this.alertCreateOrUpdateContract = {
                        type: 'error',
                        message: 'Ocurrio un error al actualizar el contrato.'
                    };
                }
            });
        } else {
            this._service.createContract(formData).pipe(finalize((): void => {
                this.contractForm.reset();
                this._showAlertCreateOrUpdateContract = true;
                this.isSubmitting = false;
                this.dialog.closeAll();
            })).subscribe({
                next: (createdContract: ContractResponse): void => {
                    this.alertCreateOrUpdateContract = {
                        type: 'success',
                        message: `El contrato con folio ${createdContract.tag} se ha creado correctamente.`
                    };
                    this.getContracts(this.paginator, this.sort);
                },
                error: (err: HttpErrorResponse): void => {
                    console.error('Error creating contract:', err.message);
                    this.alertCreateOrUpdateContract = {
                        type: 'error',
                        message: 'Ocurrio un error al crear el contrato.'
                    };
                }
            });
        }
    }

    getLinkedRegistry(contract: ContractResponse): ImssEmployerRegistryResponse | undefined {
        const registryId = contract.contractImssRegistries?.[0]?.imssEmployerRegistryId;
        return contract.imssEmployerRegistries?.find(r => r.id === registryId);
    }

    downloadTarjetaPatronal(contract: ContractResponse): void {
        const registry = this.getLinkedRegistry(contract);
        if (!registry?.altaPatronalUrl || !registry?.id) return;
        this._imssEmployerRegistryService.downloadAltaPatronal(registry.id).subscribe({
            next: (blob: Blob) => saveAs(blob, `tarjeta-patronal-${registry.rp}.pdf`),
            error: () => {
                this.alertDownloadContract = {type: 'error', message: 'No se pudo descargar la Tarjeta Patronal.'};
                this.showAlertDownloadContract = true;
            }
        });
    }

    downloadContract(fileName: string): void {
        this.showAlertDownloadContract = false;
        this._service.downloadContract(fileName).pipe(finalize((): void => {
            this.showAlertDownloadContract = true;
        })).subscribe({
            next: (response: Blob): void => {
                saveAs(response, fileName);
                this.alertDownloadContract = {
                    type: 'success',
                    message: 'El archivo ' + fileName + ' se ha descargado correctamente.'
                };
            }, error: (err: HttpErrorResponse): void => {
                console.error('Error downloading contract:', err.message);
                this.alertDownloadContract = {
                    type: 'error',
                    message: 'El archivo ' + fileName + ' no tiene información.'
                };
            }
        });
    }

    deleteContract(contract: ContractResponse): void {
        const confirmation: MatDialogRef<FuseConfirmationDialogComponent> = this._fuseConfirmationService.open({
            title: `Eliminar contrato ${contract.tag ?? ''}`,
            message: '¿Está seguro de que desea eliminar este contrato? ¡Esta acción no se puede deshacer!',
            actions: {confirm: {label: 'Aceptar'}, cancel: {label: 'Cancelar'}}
        });

        confirmation.afterClosed().subscribe((result): void => {
            if (result === 'confirmed') {
                this._service.deleteContract(contract.id).pipe(finalize((): void => {
                    this.showAlertDeleteContract = true;
                })).subscribe({
                    next: (): void => {
                        this.alertDeleteContract = {
                            type: 'success',
                            message: `El contrato con folio ${contract.tag} se ha eliminado correctamente.`
                        };
                        this.getContracts(this.paginator, this.sort);
                    },
                    error: (err: HttpErrorResponse): void => {
                        console.error('Error deleting contract:', err.message);
                        this.alertDeleteContract = {
                            type: 'error',
                            message: 'Ocurrio un error al eliminar el contrato.'
                        };
                    }
                });
            }
        });
    }

    modifyRpStatus(id: number, rpStatusToSet: RPStatus): void {
        this.showAlertSetRpStatus = false;
        this._service.setRpStatus(id, rpStatusToSet).pipe(finalize((): void => {
            this.showAlertSetRpStatus = true;
        })).subscribe({
            next: (response: RPStatus): void => {
                const contract: ContractResponse = this.contracts.find(c => c.id === id);
                const newRpStatus: RPStatus = response;
                contract.isImssEmployerRegistryRequested = newRpStatus;

                let message: string = '';
                let type: FuseAlertType;
                switch (newRpStatus) {
                    case RP_STATUS.withoutRp:
                        message = `Se ha cancelado la solicitud de asignación del Registro Patronal para el contrato con folio. ${contract.tag}`;
                        type = 'warning';
                        break;
                    case RP_STATUS.rpPending:
                        message = `Se ha solicitado la asignación del Registro Patronal para el contrato con folio. ${contract.tag}`;
                        type = 'success';
                        break;
                }

                this.alertRpStatus = {type, message};
            },
            error: (err: HttpErrorResponse): void => {
                console.error('Error updating RP status:', err.message);
                this.alertRpStatus = {
                    type: 'error',
                    message: 'Ocurrio un error al actualizar el estatus del Registro Patronal.'
                };
            }
        });
    }

    addRpToContract(contractId: number): void {
        if (this.addRpForm.invalid) {
            console.error('Add RP form is invalid');
            return;
        }

        this.isSubmitting = true;
        const rpId: number = this.addRpForm.get('rp')?.value;
        this._service.addRpToContract(contractId, rpId).pipe(finalize((): void => {
            this.showAlertCreateOrUpdateContract = true;
            this.isSubmitting = false;
        })).subscribe({
            next: (updatedContract: ContractResponse): void => {
                this.alertCreateOrUpdateContract = {
                    type: 'success',
                    message: `El Registro Patronal se ha agregado correctamente al contrato con folio ${updatedContract.tag}.`
                };
                this.dialog.closeAll();
                this.getContracts(this.paginator, this.sort);
            },
            error: (err: HttpErrorResponse): void => {
                console.error('Error adding RP to contract:', err.message);
                let message: string = 'Ocurrió un error al agregar el Registro Patronal al contrato.';
                if (err.status === HttpStatusCode.BadRequest)
                    message = 'Este registro no tiene Alta Patronal adjunta y no puede vincularse a un contrato.';

                this.alertCreateOrUpdateContract = {type: 'error', message};
            }
        });
    }

    openFianzasDialog(contract: ContractResponse): void {
        this.selectedContractUuid = contract.uuid;
        this.selectedContractTag = contract.tag;
        this.dialog.open(this.fianzasDialogTemplate, {
            width: '90%',
            maxWidth: '1200px',
            panelClass: 'ghost-white-dialog'
        });
    }

    openToggleContractFormDialogTemplate(data: ContractResponse = null): void {
        this.isUpdate = !!data;
        const dialogRef = this.dialog.open(this.toggleContractFormDialogTemplate, {
            data,
            width: '50%',
            panelClass: 'ghost-white-dialog'
        });
        dialogRef.afterOpened().subscribe((): void => this.preloadContractForm(data));
    }

    openToggleAddRpToContractDialogTemplate(data: ContractResponse): void {
        const linkedRegistry: ImssEmployerRegistryResponse | undefined = data.imssEmployerRegistries?.[0];
        this.addRpForm.patchValue({
            rp: linkedRegistry?.altaPatronalUrl ? linkedRegistry.id : ''
        });
        this.dialog.open(this.toggleAddRpToContractDialogTemplate, {
            data,
            width: '40%',
            panelClass: 'ghost-white-dialog'
        });
    }

    getStatusColor(status: number): string {
        switch (status) {
            case CONTRACT_STATUS.active:
                return '#18D25F';
            case CONTRACT_STATUS.expired:
                return '#FF181F';
            default:
                return 'gray';
        }
    }

    getStatusLabel(status: number): string {
        switch (status) {
            case CONTRACT_STATUS.active:
                return 'Activo';
            case CONTRACT_STATUS.expired:
                return 'Vencido';
            default:
                return 'Desconocido';
        }
    }

    private preloadContractForm(data?: ContractResponse): void {
        if (this.isUpdate) {
            this.contractForm.get('id').setValidators([Validators.required]);
            this.contractForm.get('uuid').setValidators([Validators.required]);

            this.contractForm.patchValue({
                id: data.id,
                uuid: data.uuid,
                tag: data.tag,
                rp: data.imssEmployerRegistries[0],
                isImssEmployerRegistryRequested: data.isImssEmployerRegistryRequested ?? RP_STATUS.withoutRp,
                startDate: moment(data.startDate).toDate(),
                endDate: moment(data.endDate).toDate(),
                region: data.region?.id ? data.region.id : 0,
                status: data.status?.id,
                amount: data.amount,
                subject: data.subject,
                file: undefined,
                synchronizeEvidenceConfig: false,
                recalculateEvidenceSummaries: false
            });
        } else {
            this.contractForm.get('id').removeValidators([Validators.required]);
            this.contractForm.get('uuid').removeValidators([Validators.required]);

            this.contractForm.patchValue({
                id: '',
                uuid: '',
                rp: '',
                isImssEmployerRegistryRequested: RP_STATUS.withoutRp,
                tag: '',
                region: 0,
                status: CONTRACT_STATUS.active,
                startDate: '',
                endDate: '',
                amount: '',
                subject: '',
                file: undefined,
                synchronizeEvidenceConfig: false,
                recalculateEvidenceSummaries: false
            });
        }

        this.toggleRecalculateEvidenceSummariesControl(this.contractForm.get('synchronizeEvidenceConfig')?.value);
        this.contractForm.updateValueAndValidity();
        this._cdr.markForCheck();
    }

    private toggleRecalculateEvidenceSummariesControl(synchronizeEvidenceConfig: boolean): void {
        const recalculateEvidenceSummariesControl = this.contractForm.get('recalculateEvidenceSummaries');

        if (!recalculateEvidenceSummariesControl)
            return;


        if (synchronizeEvidenceConfig) {
            recalculateEvidenceSummariesControl.enable({emitEvent: false});
            return;
        }

        recalculateEvidenceSummariesControl.setValue(false, {emitEvent: false});
        recalculateEvidenceSummariesControl.disable({emitEvent: false});
    }

    private getContracts(paginator: MatPaginator, sortState: MatSort): void {
        if (!this.contracteeId || !this.contractor.id) return;
        this._service.getContracts(this.contracteeId, Number(this.contractor.id), paginator, sortState).subscribe({
            next: (contracts: Page<ContractResponse>): void => {
                this.contracts = contracts.content;
                this.totalContracts = contracts.totalElements ?? 0;
                this._cdr.markForCheck();
            },
            error: (err: HttpErrorResponse): void => {
                console.error('Error fetching contracts:', err.message);
            }
        });
    }

    private getRpList(): void {
        this._imssEmployerRegistryService.getImssEmployerRegistries().subscribe({
            next: (rpList: ImssEmployerRegistryResponse[]): void => {
                this.imssEmployerRegistries = rpList;
            },
            error: (err: HttpErrorResponse): void => {
                console.error('Error fetching RP list:', err.message);
            }
        });
    }

    private resetSortAndPaginator(): void {
        if (this.paginator) this.paginator.pageIndex = 0;
        if (this.sort) {
            this.sort.active = '';
            this.sort.direction = '';
        }
    }

    private readonly dateRangeValidator = (fg: UntypedFormGroup): { dateRange: boolean } | null => {
        const init = fg.get('startDate')?.value;
        const end = fg.get('endDate')?.value;
        if (!init || !end) return null;
        return new Date(init) <= new Date(end) ? null : {dateRange: true};
    };

    private isValidProviderStatus(status: unknown): status is ProviderStatus {
        const providerStatusValues: ProviderStatus[] = Object.values(ProviderStatus)
            .filter((value: string | ProviderStatus): value is ProviderStatus => typeof value === 'number');

        return typeof status === 'number'
            && providerStatusValues.includes(status)
            && status !== ProviderStatus.INACTIVE;
    }
}
